import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdCalendarMonth,
  MdNumbers,
  MdOutlineLocalHospital,
  MdOutlineSouthAmerica,
  MdPercent,
  MdWorkOutline,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  JOB_LIST,
  PROFILE_LIST,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

export const showExerciseFrame = (value) =>
  [
    SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
    SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
    SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  ].includes(value);

const Activity = ({
  setPercent,
  disabled,
  initialValues,
  isFieldRequired,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const profilePath = "project.contact.fields.profile";
  const profileField = bindField(profilePath, {
    label: SUBSCRIPTION.FORM.PROFILE.LABEL,
    required: isFieldRequired(profilePath),
    disabled:
      get(disabled, profilePath) || !isEmpty(get(initialValues, profilePath)),
    onBlur: () => setPercent(initialValues),
  });

  const professionPath = "project.contact.profession";
  const professionField = bindField(professionPath, {
    label: SUBSCRIPTION.FORM.PROFESSION.LABEL,
    required: isFieldRequired(professionPath),
    onBlur: () => setPercent(initialValues),
  });

  const entryDatePath = "project.contact.fields.entryDate";
  const entryDateField = bindField(entryDatePath, {
    label: SUBSCRIPTION.FORM.ENTRY_DATE.LABEL,
    required: isFieldRequired(entryDatePath),
    onBlur: () => setPercent(initialValues),
  });

  const optMadelinPath = "project.contact.optMadelin";
  const optMadelinField = bindField(optMadelinPath, {
    label: SUBSCRIPTION.FORM.MADELIN_LAW.LABEL,
    required: isFieldRequired(optMadelinPath),
    onChange: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={profileField.path}
              label={profileField.label}
              required={profileField.required}
              disabled={profileField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(PROFILE_LIST).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.PROFILE.OPTIONS[key],
                    value,
                  }))}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFILE.PLACEHOLDER}
                  label={profileField.label}
                  defaultValue={profileField.defaultValue}
                  {...field}
                  onChange={profileField.onChange}
                  onBlur={profileField.onBlur}
                  optional={
                    !profileField.disabled &&
                    !profileField.required &&
                    SUBSCRIPTION.FORM.OPTIONAL
                  }
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={professionField.path}
              label={professionField.label}
              required={professionField.required}
              disabled={professionField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(JOB_LIST).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.PROFESSION.LIST[key],
                    value,
                  }))}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROFESSION.PLACEHOLDER}
                  label={professionField.label}
                  defaultValue={professionField.defaultValue}
                  {...field}
                  onChange={professionField.onChange}
                  onBlur={professionField.onBlur}
                  optional={
                    !professionField.required && SUBSCRIPTION.FORM.OPTIONAL
                  }
                />
              )}
            </FormField>
          </Col>
          {get(initialValues, professionPath) ===
            JOB_LIST.HOSPITAL_CIVIL_SERVICE && (
            <>
              <Col lg={24} xs={24}>
                <Input
                  type="number"
                  icon={<MdNumbers size={16} />}
                  label={SUBSCRIPTION.FORM.CAPPED_INDEX.LABEL}
                  defaultValue={get(
                    initialValues,
                    "project.contact.fields.cappedIndex",
                  )}
                  disabled={true}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Select
                  icon={<MdPercent size={16} />}
                  label={SUBSCRIPTION.FORM.ACTIVITY_TIME.LABEL}
                  defaultValue={get(
                    initialValues,
                    "project.contact.fields.activityTime",
                  )}
                  disabled={true}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Select
                  icon={<MdOutlineLocalHospital size={16} />}
                  label={SUBSCRIPTION.FORM.ESTABLISHMENT.LABEL}
                  defaultValue={get(
                    initialValues,
                    "project.contact.fields.establishment",
                  )}
                  disabled={true}
                />
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={entryDateField.path}
                  label={entryDateField.label}
                  required={entryDateField.required}
                  disabled={entryDateField.disabled}
                >
                  {(field) => (
                    <Input
                      type="date"
                      variant="simple"
                      optional
                      label={entryDateField.label}
                      icon={<MdCalendarMonth size={16} />}
                      placeholder={SUBSCRIPTION.FORM.ENTRY_DATE.PLACEHOLDER}
                      defaultValue={
                        get(initialValues, entryDatePath) &&
                        dayjs(get(initialValues, entryDatePath))
                      }
                      format={DATE_FORMAT}
                      disabledDate={(current) =>
                        current >= dayjs().startOf("day")
                      }
                      {...field}
                      onChange={entryDateField.onChange}
                      onBlur={entryDateField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
          {!get(disabled, optMadelinPath) && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={optMadelinField.path}
                  label={optMadelinField.label}
                  required={optMadelinField.required}
                  disabled={optMadelinField.disabled}
                >
                  {(field) => (
                    <Input
                      type="radio"
                      variant="group"
                      optional={SUBSCRIPTION.FORM.OPTIONAL}
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={optMadelinField.label}
                      values={[
                        {
                          value: true,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.YES,
                        },
                        {
                          value: false,
                          label: SUBSCRIPTION.FORM.MADELIN_LAW.OPTIONS.NO,
                        },
                      ]}
                      value={get(initialValues, optMadelinPath)}
                      {...field}
                      onChange={optMadelinField.onChange}
                      onBlur={optMadelinField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={SUBSCRIPTION.FORM.MADELIN_LAW.INFO}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
